import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Tooltip
} from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletIcon,
  FormatListNumbered as NumberedIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  Title as HeaderIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import './RichTextEditor.css';

const RichTextEditor = ({
  value,
  onChange,
  placeholder = "Enter description...",
  maxLength = 1500,
  label = "Description",
  error = false,
  helperText = ""
}) => {
  const editorRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // Initialize content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Get text length (without HTML tags)
  const getTextLength = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return div.textContent?.length || 0;
  };

  // Handle content change
  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      const textLength = getTextLength(content);

      if (textLength <= maxLength) {
        onChange(content);
      } else {
        // Revert to previous content if over limit
        editorRef.current.innerHTML = value || '';
      }
    }
  };

  // Execute formatting command
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  // Check if command is active
  const isCommandActive = (command) => {
    return document.queryCommandState(command);
  };

  const currentLength = getTextLength(value);
  const isOverLimit = currentLength > maxLength;

  return (
    <Box className="rich-text-editor-container">
      {/* Label */}
      <Typography
        variant="body2"
        className={`rich-text-label ${error ? 'error' : ''}`}
      >
        {label}
      </Typography>

      {/* Editor Wrapper */}
      <Box className={`rich-text-wrapper ${error ? 'error' : ''} ${isFocused ? 'focused' : ''}`}>
        {/* Toolbar */}
        <Box className="rich-text-toolbar">
          {/* Headers */}
          <Tooltip title="Heading 1">
            <IconButton
              size="small"
              onClick={() => execCommand('formatBlock', 'h1')}
              className="toolbar-btn"
            >
              <HeaderIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Heading 2">
            <IconButton
              size="small"
              onClick={() => execCommand('formatBlock', 'h2')}
              className="toolbar-btn"
            >
              <HeaderIcon fontSize="small" style={{ fontSize: '18px' }} />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Text Formatting */}
          <Tooltip title="Bold">
            <IconButton
              size="small"
              onClick={() => execCommand('bold')}
              className={`toolbar-btn ${isCommandActive('bold') ? 'active' : ''}`}
            >
              <BoldIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Italic">
            <IconButton
              size="small"
              onClick={() => execCommand('italic')}
              className={`toolbar-btn ${isCommandActive('italic') ? 'active' : ''}`}
            >
              <ItalicIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Underline">
            <IconButton
              size="small"
              onClick={() => execCommand('underline')}
              className={`toolbar-btn ${isCommandActive('underline') ? 'active' : ''}`}
            >
              <UnderlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Lists */}
          <Tooltip title="Bullet List">
            <IconButton
              size="small"
              onClick={() => execCommand('insertUnorderedList')}
              className="toolbar-btn"
            >
              <BulletIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Numbered List">
            <IconButton
              size="small"
              onClick={() => execCommand('insertOrderedList')}
              className="toolbar-btn"
            >
              <NumberedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem />

          {/* Alignment */}
          <Tooltip title="Align Left">
            <IconButton
              size="small"
              onClick={() => execCommand('justifyLeft')}
              className="toolbar-btn"
            >
              <AlignLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Align Center">
            <IconButton
              size="small"
              onClick={() => execCommand('justifyCenter')}
              className="toolbar-btn"
            >
              <AlignCenterIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Align Right">
            <IconButton
              size="small"
              onClick={() => execCommand('justifyRight')}
              className="toolbar-btn"
            >
              <AlignRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Editor */}
        <Box
          ref={editorRef}
          className="rich-text-editor"
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          data-placeholder={placeholder}
        />
      </Box>

      {/* Character Counter and Helper Text */}
      <Box className="rich-text-footer">
        <Typography
          variant="caption"
          className={`character-counter ${isOverLimit ? 'error' : ''}`}
        >
          {currentLength}/{maxLength} characters
        </Typography>

        {helperText && (
          <Typography
            variant="caption"
            className={`helper-text ${error ? 'error' : ''}`}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RichTextEditor;
